import React, { useEffect, useRef, useState } from "react";
import icons from "../../../../../assets/images/icons";
import { useIsDesktop } from "../../../../../common/hooks/useIsDesktop";
import styles from "./MessageInput.module.scss";

/**
 * Message input (Figma node 40004342:65566)
 * disco 디자인 v3.0.0 - 채팅 메시지 입력 영역
 * Code Connect: MessageInput.figma.jsx
 */
function MessageInput({
  className = "",
  placeholder = "문의할 메시지를 입력해 주세요",
  /** ({ text: string, files: File[] }) — 텍스트·첨부를 한 번에 전달 (MESSAGE.SEND 단일 프레임) */
  onSendClick,
  disabled = false,
  blockedByMe,
  blockedByOther,
  onUnblock,
}) {
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [draft, setDraft] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (isDesktop && !disabled) textareaRef.current?.focus();
  }, [isDesktop]);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length) setPendingFiles((prev) => [...prev, ...selected]);
    e.target.value = "";
    textareaRef.current?.focus();
  };

  const hasFiles = (e) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

  const handleDragOver = (e) => {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e) => {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files ?? []);
    if (dropped.length) setPendingFiles((prev) => [...prev, ...dropped]);
    textareaRef.current?.focus();
  };

  const removeFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    const text = draft.trim();
    const files = [...pendingFiles];
    if (!text && !files.length) return;
    onSendClick?.({ text, files });
    setDraft("");
    setPendingFiles([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.focus();
    }
  };

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !disabled && (pendingFiles.length > 0 || !!draft.trim());

  if (blockedByMe) {
    return (
      <div className={`${styles.blocked} ${className}`.trim()} data-node-id="40005878:106733">
        <div className={styles.blockedGradient} />
        <div className={styles.blockedContent}>
          <p className={styles.blockedDesc}>차단한 사용자와는 대화할 수 없습니다.</p>
          <button type="button" className={styles.blockedUnblockBtn} onClick={onUnblock}>
            차단 해제
          </button>
        </div>
      </div>
    );
  }

  // 상대방이 나를 차단한 경우: 메시지 전송 자체가 불가 (해제 버튼 없음)
  if (blockedByOther) {
    return (
      <div className={`${styles.blocked} ${className}`.trim()}>
        <div className={styles.blockedContentByOther}>
          <p className={styles.blockedDesc}>상대방이 대화를 차단한 상태입니다</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.root} ${className}`.trim()}
      data-name="Message input"
      data-node-id="40004342:65566"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className={styles.row}>
        <div className={styles.leading}>
          <div
            className={`${styles.attachHitTarget} ${disabled ? styles.attachHitTargetDisabled : ""}`.trim()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
              multiple
              className={styles.fileInputOverlay}
              onChange={handleFileChange}
              aria-label="첨부"
            />
            <span className={styles.attachIconWrap} aria-hidden>
              <img src={icons.messager?.attach} alt="" className={styles.iconImg} />
            </span>
          </div>
        </div>
        <div className={styles.inputWrap}>
          <div
            className={styles.input}
            onClick={() => {
              if (!disabled && isDesktop) textareaRef.current?.focus();
            }}
            role="presentation"
          >
            {pendingFiles.length > 0 && (
              <div className={styles.filePreviewList}>
                {pendingFiles.map((file, i) => (
                  <div key={i} className={styles.filePreview}>
                    <span className={styles.filePreviewName}>{file.name}</span>
                    <button
                      type="button"
                      className={styles.filePreviewClear}
                      onClick={() => removeFile(i)}
                      aria-label="파일 선택 취소"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              className={styles.inputInner}
              placeholder={pendingFiles.length ? "" : placeholder}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                autoResize();
              }}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={disabled}
              autoFocus={isDesktop}
              aria-label="메시지 입력"
            />
          </div>
        </div>
        <div className={styles.trailing}>
          <button
            type="button"
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={!canSend}
            aria-label="보내기"
          >
            <img
              src={icons.messager?.send}
              alt="전송"
              className={styles.iconImg}
              width={18}
              height={18}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default MessageInput;
